import Link from '@tiptap/extension-link';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import cx from 'classnames';
import React, { useCallback } from 'react';
import localeLookup from '../config/locale';
import Button from './Button';
import Input from './formElements/Input';
import Icon from './Icon';
import Tooltip from './Tooltip';

const PasteHandler = Extension.create({
  name: 'pasteHandler',
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: new PluginKey('pasteHandler'),
        props: {
          handlePaste(view, event) {
            const html = event.clipboardData?.getData('text/html');
            if (!html?.includes('<a')) return false;

            const div = document.createElement('div');
            div.innerHTML = html;
            const links = div.querySelectorAll('a[href]');
            
            if (links.length === 0) return false;
            
            event.preventDefault();
            links.forEach((link) => {
              const href = link.getAttribute('href');
              const linkText = link.textContent || '';
              if (href && linkText) {
                editor.chain().focus().insertContent({
                  type: 'text',
                  text: linkText,
                  marks: [{ type: 'link', attrs: { href } }],
                }).run();
              }
            });
            return true;
          },
        },
      }),
    ];
  },
});

const extensions = [
  TextStyleKit,
  StarterKit,
  PasteHandler,
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: 'https',
    protocols: ['http', 'https'],
    isAllowedUri: (url, ctx) => {
      try {
        // construct URL
        const parsedUrl = url.includes(':')
          ? new URL(url)
          : new URL(`${ctx.defaultProtocol}://${url}`);
        // use default validation
        if (!ctx.defaultValidate(parsedUrl.href)) {
          return false;
        }

        // disallowed protocols
        const disallowedProtocols = ['ftp', 'file', 'mailto'];
        const protocol = parsedUrl.protocol.replace(':', '');

        if (disallowedProtocols.includes(protocol)) {
          return false;
        }

        // only allow protocols specified in ctx.protocols
        const allowedProtocols = ctx.protocols.map((p) =>
          typeof p === 'string' ? p : p.scheme
        );

        if (!allowedProtocols.includes(protocol)) {
          return false;
        }

        // disallowed domains
        const disallowedDomains = [
          'example-phishing.com',
          'malicious-site.net',
        ];
        const domain = parsedUrl.hostname;

        if (disallowedDomains.includes(domain)) {
          return false;
        }

        // all checks have passed
        return true;
      } catch {
        return false;
      }
    },
    shouldAutoLink: (url) => {
      try {
        // construct URL
        const parsedUrl = url.includes(':')
          ? new URL(url)
          : new URL(`https://${url}`);

        // only auto-link if the domain is not in the disallowed list
        const disallowedDomains = [
          'example-no-autolink.com',
          'another-no-autolink.com',
        ];
        const domain = parsedUrl.hostname;

        return !disallowedDomains.includes(domain);
      } catch {
        return false;
      }
    },
  }),
];

function MenuBar({ editor, showLinkInput, setShowLinkInput }) {
  const [linkUrl, setLinkUrl] = React.useState('');
  const [linkTitle, setLinkTitle] = React.useState('');
  const [isUpdatingLink, setIsUpdatingLink] = React.useState(false);
  //const [showLinkInput, setShowLinkInput] = React.useState(false);

  const getLinkText = useCallback(() => {
    // Get the current selection
    const { state } = editor;
    const { from, to } = state.selection;
    if (editor.isActive('link')) {
      // If nothing is selected, extend the selection to the full link
      if (from === to) {
        editor.chain().extendMarkRange('link').run();
        const newSelection = editor.state.selection;
        const linkText = editor.state.doc.textBetween(
          newSelection.from,
          newSelection.to,
          ' '
        );

        // Restore the original cursor position
        editor.chain().setTextSelection(from).run();

        return linkText;
      } else {
        // Text is already selected
        return state.doc.textBetween(from, to, ' ');
      }
    } else {
      // Not in a link - use any selected text
      return state.doc.textBetween(from, to, ' ');
    }
  }, [editor]);

  const toggleLink = useCallback(() => {
    const linkText = getLinkText();
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl);
    setLinkTitle(linkText);
    if (linkText) {
      setIsUpdatingLink(true);
    } else {
      setIsUpdatingLink(false);
    }
    setShowLinkInput((prev) => !prev);
  }, [editor, setShowLinkInput, getLinkText]);

  const setLink = useCallback((e) => {
    e.preventDefault();
    const url = linkUrl?.trim();

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setShowLinkInput(false);
      setLinkTitle('');
      setLinkUrl('');
      return;
    }

    const parsedUrl = url?.includes(':')
      ? new URL(url)
      : new URL(`https://${url}`);

    try {
      const { state } = editor;
      const { from, to } = state.selection;
      const isLinkActive = editor.isActive('link');

      if (isUpdatingLink && isLinkActive) {
        // Updating an existing link
        if (linkTitle && linkTitle.trim() !== '') {
          // User provided a new title - replace the link text and URL
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .insertContent({
              type: 'text',
              text: linkTitle,
              marks: [{ type: 'link', attrs: { href: parsedUrl.href } }],
            })
            .run();
        } else {
          // No title provided - just update the URL, keep existing text
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: parsedUrl.href })
            .run();
        }
      } else if (
        !isUpdatingLink &&
        linkTitle &&
        linkTitle.trim() !== '' &&
        from === to
      ) {
        // Creating a new link with no text selected - insert new text
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: linkTitle,
            marks: [{ type: 'link', attrs: { href: parsedUrl.href } }],
          })
          .run();
      } else {
        // Default behavior - apply link to selected text or update existing link URL only
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: parsedUrl.href })
          .run();
      }

      setShowLinkInput(false);
      setLinkTitle('');
      setLinkUrl('');
      setIsUpdatingLink(false);
    } catch (e) {
      alert(e.message);
    }
  }, [editor, linkUrl, linkTitle, isUpdatingLink]);

  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isCode: ctx.editor.isActive('code') ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        //isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
        //isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
        //isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        isLink: ctx.editor.isActive('link'),
      };
    },
  });

  return (
    <div className="rich-text-editor__menu-bar">
      <div className="rich-text-editor__menu-bar-buttons">
        <Tooltip tooltip={localeLookup('translations.Bold')}>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editorState.canBold}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isBold,
            })}
          >
            <Icon kind="bold"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Italic')}>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editorState.canItalic}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isItalic,
            })}
          >
            <Icon kind="italic"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Underline')}>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!editorState.canUnderline}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isUnderline,
            })}
          >
            <Icon kind="underline"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Strikethrough')}>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editorState.canStrike}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isStrike,
            })}
          >
            <Icon kind="strikethrough"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Bullet list')}>
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isBulletList,
            })}
          >
            <Icon kind="list"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Number list')}>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isOrderedList,
            })}
          >
            <Icon kind="list2"></Icon>
          </button>
        </Tooltip>

        <Tooltip tooltip={localeLookup('translations.Insert/edit link')}>
          <button
            onClick={toggleLink}
            className={cx('rich-text-editor__menu-bar-button', {
              'rich-text-editor__menu-bar-button--is-active':
                editorState.isLink,
            })}
          >
            <Icon kind="link"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Remove link')}>
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editorState.isLink}
            className="rich-text-editor__menu-bar-button"
          >
            <Icon kind="unlink"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Clear formatting')}>
          <button
            className="rich-text-editor__menu-bar-button"
            onClick={() => {
              editor.chain().focus().clearNodes().run();
              editor.chain().focus().unsetAllMarks().run();
            }}
          >
            <Icon kind="text-format-remove"></Icon>
          </button>
        </Tooltip>
        {/* <button
          className="rich-text-editor__menu-bar-button"
          onClick={() => editor.chain().focus().clearNodes().run()}
          >
          <Icon kind="eraser"></Icon>
          </button> */}
        <Tooltip tooltip={localeLookup('translations.Undo')}>
          <button
            className="rich-text-editor__menu-bar-button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editorState.canUndo}
          >
            <Icon kind="undo2"></Icon>
          </button>
        </Tooltip>
        <Tooltip tooltip={localeLookup('translations.Redo')}>
          <button
            className="rich-text-editor__menu-bar-button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editorState.canRedo}
          >
            <Icon kind="redo2"></Icon>
          </button>
        </Tooltip>
      </div>
      {showLinkInput && (
        <form
          onSubmit={setLink}
          className="rich-text-editor__menu-bar-link-input"
        >
          <Input
            wrapperClassName="rich-text-editor__menu-bar-link-input-wrapper"
            className="rich-text-editor__menu-bar-link-input-field"
            type="text"
            autoFocus
            placeholder={localeLookup('translations.Title')}
            size="small"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
          />
          <Input
            wrapperClassName="rich-text-editor__menu-bar-link-input-wrapper"
            className="rich-text-editor__menu-bar-link-input-field"
            type="text"
            placeholder="URL"
            size="small"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <Button
            className="rich-text-editor__menu-bar-link-input-button"
            kind="darkui"
            size="small"
            type="submit"
            onClick={setLink}
          >
            {localeLookup('translations.Save')}
          </Button>
          <Button
            className="rich-text-editor__menu-bar-link-input-button"
            kind="cancel"
            size="small"
            onClick={toggleLink}
          >
            {localeLookup('translations.Cancel')}
          </Button>
        </form>
      )}
    </div>
  );
}

export default ({ autoFocus, onChange, defaultValue }) => {
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const editor = useEditor({
    extensions,
    content: defaultValue,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!showLinkInput);
    }
  }, [editor, showLinkInput]);

  return (
    <div className="rich-text-editor">
      <MenuBar
        editor={editor}
        showLinkInput={showLinkInput}
        setShowLinkInput={setShowLinkInput}
      />
      <EditorContent
        className={cx('rich-text-editor__content', {
          'rich-text-editor__content--disabled': showLinkInput,
        })}
        editor={editor}
      />
    </div>
  );
};
